import { useState } from 'react';
import { useUserState } from '../../hooks/context/useUserState';
import { useResponseActionsContext } from '../../hooks/context/useResponseActionsContext';
import useApiErrorHandling from '../../hooks/useApiErrorHandling';
import { requestLayout } from '../requests/layout/requestLayout';
import { apiRequest } from '../utils/apiRequest';
import { detailedApiMethodFetcher } from '../utils/fetchers/detailedApiMethodFetcher';
import { HandlerRequestContext } from '../types/types';
import { DetailedApiMethod, Layout } from '../../types/types';

export const useLayout = () => {
  const {
    state: { token },
  } = useUserState();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [data, setData] = useState<Layout | undefined>(undefined);
  const handleApiError = useApiErrorHandling();
  const { getResponseActionHandler } = useResponseActionsContext();

  const fetch = async (
    api: string,
    forceHeader?: string
  ): Promise<Layout | undefined> => {
    try {
      setIsLoading(true);
      // Data stay until the fresh ones arrive — the post-save reload must not
      // blank (and remount) the editor.
      setError(undefined);
      const layout = await requestLayout(api, token, forceHeader);
      setData(layout);
      return layout;
    } catch (e) {
      if (e instanceof Error) setError(e);
      handleApiError(e, true, (retryForceHeader) =>
        fetch(api, retryForceHeader)
      );
    } finally {
      setIsLoading(false);
    }
  };

  // apiRequest resolves the {{ }} templates against resolveData, fires the
  // request and runs the method's responseActions on the response.
  const detailedFetch = async (
    apiMethod: DetailedApiMethod,
    resolveData: HandlerRequestContext,
    forceHeader?: string
  ): Promise<Layout | undefined> => {
    try {
      setIsLoading(true);
      // Data stay until the fresh ones arrive — the post-save reload must not
      // blank (and remount) the editor.
      setError(undefined);
      const layout = await apiRequest<Layout>(
        apiMethod,
        resolveData,
        (resolvedMethod) =>
          detailedApiMethodFetcher<Layout>(resolvedMethod, forceHeader),
        getResponseActionHandler
      );
      setData(layout);
      return layout;
    } catch (e) {
      if (e instanceof Error) setError(e);
      handleApiError(e, true, (retryForceHeader) =>
        detailedFetch(apiMethod, resolveData, retryForceHeader)
      );
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, error, data, fetch, detailedFetch };
};
